<template>
	<SidePanelContent class="w-[30rem]">
		<template #heading>
			<div class="flex items-center gap-04">
				<Icon name="star-outline" size="2" />

				<span>
					{{ t('favouriteSKUsPanel.title') }}
				</span>
			</div>
		</template>

		<BaseInput
			v-model="searchString"
			prefix-icon="magnify"
			class="mb-04"
			:placeholder="t('favouriteSKUsPanel.search')"
			with-clear
			@keydown.esc="searchString = ''"
		/>
		<div class="stores flex flex-col">
			<FavouriteStore v-for="store in stores" :id="store.id" :key="store.id" :store="store" :filter-string="searchString" />
		</div>

		<template #footer>
			<div class="flex items-center justify-end gap-03">
				<Button variant="tertiary" :disabled="isSavingFavouriteSKUs" @click="cancel()">
					{{ t('favouriteSKUsPanel.cancel') }}
				</Button>

				<Tooltip :content="saveButtonTooltip">
					<Button variant="primary" :disabled="changedSKUIds.size === 0" :loading="isSavingFavouriteSKUs" @click="save()">{{
						t('favouriteSKUsPanel.save')
					}}</Button>
				</Tooltip>
			</div>
		</template>
	</SidePanelContent>
</template>

<script setup lang="ts">
import { BaseInput, Button, Icon, Tooltip, useTranslation } from '@bluebrick/saas-ui'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useEventsStore } from '@/features/events/useEventsStore'
import SidePanelContent from '@/features/sidePanel/components/SidePanelContent.vue'
import { useSidebarStore } from '@/features/sidePanel/useSidebarStore'
import FavouriteStore from '@/features/skus/components/FavouriteStore.vue'
import { useSkusStore } from '@/features/skus/useSkusStore'
import { useStoresStore } from '@/features/stores/useStoresStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'

const { trackEvent } = useTelemetry()
const { t } = useTranslation()
const { stores, storeForFavouriteSKUs } = storeToRefs(useStoresStore())
const { closeFavouriteSKUsSidebar } = useSidebarStore()
const { initTemporaryFavouriteSKUIds, saveFavouriteSKUs } = useSkusStore()
const { isSavingFavouriteSKUs, changedSKUIds } = storeToRefs(useSkusStore())
const { loadStoresData } = useEventsStore()

const searchString = ref('')

const saveButtonTooltip = computed(() => {
	if (changedSKUIds.value.size === 0) {
		return t('favouriteSKUsPanel.cantSaveNoChanges')
	}

	return t('favouriteSKUsPanel.savingWillReload')
})

async function save() {
	const affectedStores = await saveFavouriteSKUs()

	closeFavouriteSKUsSidebar()

	/* Reloading the stores here instead of directly in `useSkusStore.saveFavouriteSKUs` as `useEventsStore`
	already imports `useSkusStore` and this would cause a circular dependency */
	loadStoresData(affectedStores)
}

function cancel() {
	trackEvent('user', 'Closing favourite SKUs panel.')

	closeFavouriteSKUsSidebar()
}

onMounted(() => {
	initTemporaryFavouriteSKUIds()

	if (storeForFavouriteSKUs.value) {
		const el = document.getElementById(storeForFavouriteSKUs.value)

		if (el) {
			el.scrollIntoView({ behavior: 'smooth' })
		}
	}
})
</script>
